<template>
  <div class="full-page-height">
    <!-- Partial: Alerts -->
    <sc-alert
      v-if="taxReturn.status == 'action_required'"
      type="info"
      class="instruction"
    >
      <span>
        <b>Instruction:</b> Please review tax summary and ensure all information
        is correct before proceeding.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="finishReviewing()"
          icon-right="arrow-right"
        >
          Finished Reviewing
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <sc-alert v-else type="info" class="instruction">
      <span>
        Note: This page is READONLY because the tax return has been delivered.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="finishReviewing()"
          icon-left="arrow-right"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <div class="tax-content-container">
      <div v-if="editable || taxReturn.tax_details.length > 0">
        <div class="summary-header">
          <sc-h1>
            Tax Summary
          </sc-h1>
        </div>

        <div class="mb-3">
          <sc-alert type="note" v-if="editable">
            This page is readonly only. To edit, please go back to the previous
            sections.
          </sc-alert>
        </div>

        <v-container grid-list-lg>
          <v-layout row wrap class="cards-row">
            <v-flex
              md6
              sm12
              v-for="(detail, cardIndex) in taxDetails"
              :key="cardIndex"
              class="card"
            >
              <div class="tax-details-card">
                <v-layout class="card-title-container">
                  <sc-h4>{{ detail.title }}</sc-h4>
                </v-layout>

                <v-divider class="my-2"></v-divider>

                <div
                  v-for="(row, rowIndex) in detail.rows"
                  :key="rowIndex"
                  class="detail-row form-control"
                  v-if="rowOptions[row.label]"
                >
                  <div style="flex: auto;">
                    <span :class="rowOptions[row.label].cssClass">{{
                      row.label
                    }}</span>
                  </div>

                  <div>
                    <v-text-field
                      solo
                      hide-details
                      readonly
                      class="readonly-field"
                      :class="rowOptions[row.label].cssClass"
                      :value="getTrueValue(detail.title, row)"
                    />
                  </div>
                </div>
              </div>
            </v-flex>
          </v-layout>
        </v-container>
      </div>

      <!-- TODO: Temporary hide payment vouchers section. Will be removed if not used.
      <div class="mt-5 summary-header">
        <sc-h1>
          Payment Summary
        </sc-h1>
      </div>

      <div class="mb-3">
        <sc-alert type="note" v-if="editable">
          To edit payments, please go back to "Payments" section.
        </sc-alert>
      </div>

      <v-container grid-list-lg>
        <v-layout
          row
          wrap
          class="cards-row"
          v-if="Object.keys(paymentVouchersGroupedById).length > 0"
        >
          <v-flex
            md6
            sm12
            v-for="(vouchers, voucherId) in paymentVouchersGroupedById"
            :key="voucherId"
            class="card"
          >
            <div class="payment-voucher-card">
              <sc-h4>{{ getFullPaymentName(vouchers[0]) }}</sc-h4>
              <v-divider class="my-2"></v-divider>

              <div class="voucher-table">
                <div class="voucher-table-header detail-row">
                  <span>Due Date</span>
                  <span>Amount</span>
                </div>
                <div
                  v-for="(voucher, vIndex) in vouchers"
                  :key="`${voucherId}-${vIndex}`"
                  class="voucher-table-row detail-row"
                >
                  <span>
                    {{
                      voucher.is_due_asap
                        ? 'Due ASAP'
                        : voucher.due_date || 'N/A'
                    }}
                    {{
                      voucher.quarter && voucher.quarter !== 'None'
                        ? ` (${voucher.quarter})`
                        : ''
                    }}
                  </span>
                  <span>{{ formatCurrency(voucher.amount) }}</span>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
      -->

      <v-spacer class="pb-5" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CurrencyInput from '@/components/returns/CurrencyInput';
import TaxDetailMixin from '@/mixins/TaxDetailMixin';

export default {
  mixins: [TaxDetailMixin],

  components: {
    CurrencyInput,
  },

  name: 'TaxSummary',

  props: {
    taxReturn: {
      type: Object,
      required: true,
    },
    currentCompany: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      voucherDefinitions: [],
      taxDetails: [],
    };
  },

  methods: {
    getFullPaymentName(paymentVoucher) {
      let paymentName =
        paymentVoucher.voucher_id === 'other'
          ? paymentVoucher.custom_payment_name
          : this.getVoucherDefinitionById(paymentVoucher.voucher_id)
          ? `${
              this.getVoucherDefinitionById(paymentVoucher.voucher_id).name
            } (${paymentVoucher.voucher_id})`
          : 'Unknown';

      return paymentName;
    },

    getVoucherDefinitionById(id) {
      return this.voucherDefinitions.find(voucher => voucher.id === id);
    },

    async getVoucherDefinitions() {
      const response = await axios.get(`/tax_returns/get_voucher_definitions`);
      this.voucherDefinitions = response.data;
    },
  },

  computed: {
    editable() {
      return this.taxReturn.status === 'action_required';
    },

    // This value is required for TaxDetailMixin
    currentTaxDetails() {
      return this.taxDetails;
    },

    paymentVouchersGroupedById() {
      if (
        !this.taxReturn ||
        !this.taxReturn.payment_vouchers ||
        this.taxReturn.payment_vouchers.length === 0
      ) {
        return {};
      }
      return this.taxReturn.payment_vouchers.reduce((acc, voucher) => {
        let key;
        if (voucher.voucher_id === 'other') {
          key = voucher.custom_payment_name || 'Other';
        } else {
          key = voucher.voucher_id || 'Unnamed Voucher';
        }

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(voucher);

        return acc;
      }, {});
    },

    async finishReviewing() {
      if (this.editable) {
      }

      this.$emit('finish-reviewing');
    },
  },

  async mounted() {
    this.taxDetails = this.taxReturn.tax_details.slice();

    if (this.taxReturn.paper_filed_returns) {
      this.taxReturn.paper_filed_returns.forEach(paperReturn => {
        if (paperReturn.tax_details && paperReturn.tax_details.length > 0) {
          this.taxDetails = this.taxDetails.concat(paperReturn.tax_details);
        }
      });
    }

    await this.getVoucherDefinitions();
  },
};
</script>

<style scoped>
.full-page-height {
  height: 100dvh;
  overflow: auto;
}

.instruction {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
}

.tax-content-container {
  max-width: 1200px;
  margin: 32px auto;
  padding: 16px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-header h1 {
}

.cards-row {
  margin-bottom: 20px;
}

.card {
}

.tax-details-card {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  flex-grow: 1;
}

.payment-voucher-card {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  flex-grow: 1;
}

.payment-voucher-card .voucher-item-details {
  margin-bottom: 10px;
}

.payment-voucher-card .voucher-item-details:last-child {
  margin-bottom: 0;
}

.voucher-table {
  width: 100%;
}

.voucher-table-header {
  font-weight: bold;
  border-bottom: 1px solid #e0e0e0;
}

.voucher-table-row span:first-child,
.voucher-table-header span:first-child {
  text-align: left;
  flex-basis: 60%;
}

.voucher-table-row span:last-child,
.voucher-table-header span:last-child {
  text-align: right;
  flex-basis: 40%;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 15px;
  height: 48px;
}

.form-control {
  & >>> .v-input__control {
    min-height: 30px;
    font-size: 15px;
  }

  & >>> .v-text-field .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #ddd;
    border-radius: 8px;
    max-width: 120px;
  }

  & >>> .v-text-field .v-input__slot input {
    text-align: right;
  }

  & >>> .readonly-field .v-input__slot {
    background-color: #f1f1f1;
    box-shadow: none !important;
    color: #000;
  }

  & >>> .readonly-field .v-input__slot input {
    cursor: not-allowed;
  }
}

.form-control .error--text {
  & >>> .v-input__slot {
    border: 1px solid var(--red-500) !important;
  }
}

.card-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  height: 40px;
}

.editable-title-input {
  font-size: inherit;
  font-weight: inherit;
  border: 1px solid #ccc;
  padding: 2px 6px;
  border-radius: 4px;
  flex-grow: 1;
}

.amount-refunded {
  color: var(--green-700);
}

.amount-owed {
  color: var(--red-700);
}

.icon-btn {
  height: 24px;
  width: 24px;
  margin: 0 4px;
}
</style>
